<script lang="ts" setup>
import { Tooltip, Dropdown, FlashMessage } from "@/components";
import { useDocumentDownload } from "@/composables/useDocumentDownload";
import { useStore } from "vuex";
import { getStorageProxyUrl, MimeType, Formatter } from "@/util";
import { type WorkspaceDocumentFragment } from "@/graphql/operations";
import { computed, PropType } from "vue";
import { useI18n } from "vue-i18n";

const store = useStore();
const { t } = useI18n();

const props = defineProps({
  doc: {
    type: Object as PropType<WorkspaceDocumentFragment>,
    required: true,
  },
});

const emit = defineEmits(["edit", "move", "share"]);

const isAdmin = computed(
  () => store.getters.currentWorkspace?.root?.myPrivileges?.mayAdministrate,
);
const { download, downloading, downloadError } = useDocumentDownload();
const canDownload = computed(
  () => isAdmin.value || props.doc.allowUserDownload,
);
const canUseSharedSpaces = computed(() =>
  store.getters.user.canUseSharedSpaces(),
);

const getActionItemsForDocument = (doc: WorkspaceDocumentFragment) => {
  if (!isAdmin.value) {
    return [];
  }

  const items = [
    {
      value: "edit",
      title: t("pvCardDoc.tooltip.edit"),
    },
    {
      value: "move",
      title: t("pvCardDoc.tooltip.move"),
    },
  ];

  if (canUseSharedSpaces.value && doc.doesSupportSharedspace) {
    items.push({
      value: "share",
      title: t("pvCardDoc.tooltip.share"),
    });
  }

  if (canDownload.value) {
    items.push({ value: "download", title: t("pvDocEditor.download") });
  }
  return items;
};

const handleAction = (value: "edit" | "move" | "share" | "download") => {
  if (value === "download") {
    void download(props.doc.id);
  } else {
    emit(value, props.doc.id);
  }
};

const hasTags = computed(() => props.doc.tags && props.doc.tags.length > 0);
</script>

<template>
  <div class="row">
    <FlashMessage
      v-if="downloadError"
      state="error"
      :message="$t('pvDocEditor.downloadError')"
    />
    <div class="cell thumbnail_container">
      <router-link
        :to="{
          name: 'StackedProjector',
          params: { workspaceId: doc.workspaceId },
          query: { fid: doc.firestoreId },
        }"
        draggable="false"
      >
        <img
          class="thumbnail_image"
          :src="getStorageProxyUrl(doc.thumbnailDownloadUrl)"
        />
      </router-link>
    </div>
    <div class="cell grow">
      <router-link
        :to="{
          name: 'StackedProjector',
          params: { workspaceId: doc.workspaceId },
          query: { fid: doc.firestoreId },
        }"
        draggable="false"
      >
        <div class="doc_title" :class="{ top_margin: hasTags === true }">
          {{ doc.displayTitle }}
          <Tooltip v-if="doc.teaserText && doc.teaserText.length > 0">
            <template v-slot>
              <span class="material-symbols-outlined"> info </span>
            </template>
            <template #tooltip>{{ doc.teaserText }}</template>
          </Tooltip>
        </div>
        <div class="info">
          <div>
            <span>
              {{ MimeType.getName(doc.originalMimeType ?? doc.mimeType) }}
            </span>
            <span> &bull; </span>
            <template v-if="MimeType.isVideo(doc.mimeType)">
              <span>{{ Formatter.formatDuration(doc.pages) }}</span>
            </template>
            <template v-else>
              <span>{{
                $t("documentSearch.document.numPages", {
                  count: doc.pages,
                })
              }}</span>
            </template>
            <span> &bull; </span>
            <span>
              {{ Formatter.formatDate(new Date(doc.displayDate), "%Y-%m-%d") }}
            </span>
          </div>
        </div>
        <div class="tags" v-if="hasTags">
          <span class="tag" v-for="(tag, idx) in doc.tags" :key="idx">
            {{ tag }}
          </span>
        </div>
      </router-link>
    </div>
    <div class="cell menu">
      <Tooltip v-if="!isAdmin && canDownload">
        <template v-slot>
          <button
            type="button"
            class="icon_action"
            :aria-label="$t('pvDocEditor.download')"
            :disabled="downloading"
            @click.stop="download(doc.id)"
          >
            <span class="material-symbols-outlined filled">download</span>
          </button>
        </template>
        <template #tooltip>{{ $t("pvDocEditor.download") }}</template>
      </Tooltip>
      <template
        v-if="!isAdmin && canUseSharedSpaces && doc.doesSupportSharedspace"
      >
        <Tooltip>
          <template v-slot>
            <button
              type="button"
              class="icon_action"
              :aria-label="$t('pvCardDoc.tooltip.share')"
              @click.stop="handleAction('share')"
            >
              <span class="material-symbols-outlined filled"> share </span>
            </button>
          </template>
          <template #tooltip>{{ $t("pvCardDoc.tooltip.share") }}</template>
        </Tooltip>
      </template>

      <template v-else-if="isAdmin">
        <Dropdown
          class="right"
          :items="getActionItemsForDocument(doc)"
          @change="handleAction"
        >
          <span class="material-symbols-outlined">more_vert</span>
        </Dropdown>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.menu {
  width: auto;
  min-width: 30px;
  white-space: nowrap;
}

.menu .icon_action {
  appearance: none;
  background: transparent;
  border: 0;
  color: inherit;
  font: inherit;
  line-height: inherit;
  margin: 0;
  padding: 0;
}

a {
  color: var(--classic-text, #363636);
  text-decoration: none;
}

.thumbnail_container {
  height: 60px;
  width: 60px;
  padding: 0.5em 0;
}

.thumbnail_container a {
  height: 100%;
  width: 100%;
  background-color: var(--classic-media-surface, #fafafa);
  display: inline-block;
}

.draggable img.thumbnail_image {
  object-fit: contain;
  border-radius: 4%;
  background-color: var(--classic-media-surface, #fafafa);
  height: 100%;
  width: 100%;
}

.info {
  color: var(--classic-text-secondary, rgba(82, 74, 62, 0.82));
  font-size: 0.9em;
  margin-top: 0.5em;
}

.menu .material-symbols-outlined {
  font-size: 1.2em;
  padding: 0.58em;
  line-height: 1;
}

.doc_title.top_margin {
  margin-top: 0.5em;
}

.doc_title .material-symbols-outlined {
  font-size: 1.2em;
}

.tags {
  white-space: nowrap;
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 0.9em;
  margin-top: 0.6em;
  margin-bottom: 0.8em;
}

.tag {
  background-color: var(--classic-tag-surface, #eaeaea);
  padding: 0.5em 0.8em;
  border-radius: 2em;
  margin-right: 0.3em;
  display: inline-block;
  font-size: 0.9em;
}
</style>
